import { createFormattingElement } from '../../../common/utils/dom';
import { getAccessibleTextColor, getAutoAvatarColor, getUserInitials, normalizeUrl } from '../../../common/utils/common';
import { ContentType } from '../../../models/enums';
import { createElement } from '@syncfusion/ej2-base';
/**
 * `Content renderer` module is used to render content in Blocks.
 *
 * @hidden
 */
export class ContentRenderer {
    constructor(manager) {
        this.parent = manager;
    }
    /**
     * Renders the actual content of a block.
     *
     * @param {BlockModel} block - The block model containing the content.
     * @param {HTMLElement} contentElement - The HTML element where the content will be rendered.
     * @returns {void}
     * @hidden
     */
    renderContent(block, contentElement) {
        if (block.content && block.content.length > 0) {
            // clear html and render again
            if (contentElement) {
                contentElement.innerHTML = '';
            }
            block.content.forEach((content) => {
                const node = this.invokeContentRenderer(block, content);
                if (node && contentElement) {
                    contentElement.appendChild(node);
                }
            });
        }
        else {
            const emptyNode = this.renderText(null);
            if (emptyNode && contentElement) {
                contentElement.appendChild(emptyNode);
                if (!block.properties.placeholder) {
                    const br = document.createElement('br');
                    contentElement.appendChild(br);
                }
            }
        }
        this.parent.stateManager.updateManagerBlocks();
    }
    /**
     * Invokes appropriate content renderer based on content type.
     * Pure function - creates and returns nodes without side effects.
     *
     * @param {BlockModel} block - The block model
     * @param {ContentModel} contentModel - The content model to render
     * @returns {Node} - The created node
     * @hidden
     */
    invokeContentRenderer(block, contentModel) {
        let node = null;
        switch (contentModel.contentType) {
            case ContentType.Text:
                node = this.renderText(contentModel);
                break;
            case ContentType.Link:
                node = this.renderAnchor(contentModel);
                break;
            case ContentType.Mention:
                node = this.renderMention(contentModel);
                break;
            case ContentType.Label:
                node = this.renderLabel(contentModel);
                break;
        }
        // Add <br> if placeholder check is needed and content is empty
        if (node && !contentModel.content && !block.properties.placeholder) {
            const container = document.createElement('span');
            container.appendChild(node);
            const br = document.createElement('br');
            container.appendChild(br);
            return container;
        }
        return node;
    }
    /**
     * Renders text content as a pure function.
     * Creates and returns text node with applied formatting.
     *
     * @param {ContentModel} content - The content model to render
     * @returns {Node} - The created text node
     * @hidden
     */
    renderText(content) {
        if (!content) {
            return document.createTextNode('');
        }
        const formattedNode = createFormattingElement(content);
        return formattedNode;
    }
    /**
     * Renders link/anchor content as a pure function.
     * Creates and returns anchor node with applied formatting.
     *
     * @param {ContentModel} content - The content model to render
     * @returns {Node} - The created anchor node
     * @hidden
     */
    renderAnchor(content) {
        const props = content.properties;
        props.url = normalizeUrl(props.url);
        const linkData = {
            url: props.url
        };
        const formattedNode = createFormattingElement(content, linkData);
        return formattedNode;
    }
    /**
     * Renders mention/user chip as a pure function.
     * Creates and returns mention element without appending.
     *
     * @param {ContentModel} content - The content model with mention properties
     * @returns {Node} - The created mention element
     * @hidden
     */
    renderMention(content) {
        const props = content.properties;
        const userModel = this.parent.users.find((user) => user.id.toLowerCase() === props.userId);
        if (!userModel) {
            return null;
        }
        const name = userModel.user.trim();
        const initials = getUserInitials(name);
        const backgroundColor = userModel.avatarBgColor || getAutoAvatarColor(userModel.id);
        const avatarUrl = userModel.avatarUrl || '';
        const wrapper = createElement('div', {
            className: 'e-mention-chip e-user-chip',
            attrs: {
                'data-user-id': userModel.id,
                contenteditable: 'false'
            }
        });
        const avatar = createElement('div', {
            className: 'em-avatar',
            attrs: {
                style: `background-color: ${backgroundColor};`
            }
        });
        if (avatarUrl) {
            const avatarImg = createElement('img', {
                className: 'em-img',
                attrs: {
                    src: avatarUrl,
                    alt: userModel.user
                }
            });
            avatar.appendChild(avatarImg);
        }
        else {
            const initial = createElement('div', {
                className: 'em-initial'
            });
            initial.innerText = initials;
            avatar.appendChild(initial);
        }
        const userNameContent = createElement('div', {
            className: 'em-content'
        });
        userNameContent.innerText = userModel.user;
        content.content = userModel.user;
        wrapper.appendChild(avatar);
        wrapper.appendChild(userNameContent);
        return wrapper;
    }
    /**
     * Renders label/tag chip as a pure function.
     * Creates and returns label element without appending.
     *
     * @param {ContentModel} content - The content model with label properties
     * @returns {Node} - The created label element
     * @hidden
     */
    renderLabel(content) {
        const props = content.properties;
        const items = this.parent.labelSettings.items;
        const labelItem = items.find((item) => item.id === props.labelId);
        if (!labelItem) {
            return null;
        }
        const labelChip = createElement('span', {
            className: 'e-mention-chip e-label-chip',
            styles: `background: ${labelItem.labelColor};color: ${getAccessibleTextColor(labelItem.labelColor)};`,
            attrs: {
                'data-label-id': labelItem.id,
                contenteditable: 'false'
            }
        });
        labelChip.innerText = `${labelItem.groupBy}: ${labelItem.text}`;
        content.content = `${labelItem.groupBy}: ${labelItem.text}`;
        return labelChip;
    }
}
